import { useState, useCallback, useMemo } from 'react';
import { AxiosInstance } from 'axios';

import useApiRequest from 'hooks/useApiRequest';
import useAsync from 'hooks/useAsync';

interface LatLng {
  lat: number;
  lng: number;
}

interface Params {
  venueId: number;
}

export interface Venue {
  id: number;
  name: string;
  location: string;
  image: string;
  distance: number;
  latLng: LatLng;
}

interface Response {
  venues: Venue[];
}

export const getNearbyVenues = (api: AxiosInstance, { venueId }: Params) =>
  api
    .get<Response>(`/v1/venues/${venueId}/nearby`)
    .then(({ data: { venues } }) => venues);

const useNearbyVenues = (params: Params) => {
  const [venues, setVenues] = useState<Venue[] | undefined>();

  const apiRequest = useApiRequest();
  const requestValid = !!params.venueId;

  const clearData = useCallback(() => {
    setVenues([]);
  }, []);

  const jsonParams = useMemo(() => JSON.stringify(params), [params]);

  const fetchData = useCallback(async () => {
    setVenues(await getNearbyVenues(apiRequest, JSON.parse(jsonParams)));
  }, [apiRequest, jsonParams]);

  const { loading, error } = useAsync(
    requestValid ? fetchData : clearData,
    requestValid
  );

  return {
    data: venues,
    loading,
    error,
  };
};

export default useNearbyVenues;
